import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SectionHeading from "./SectionHeading";
import { experiences } from "../constants/data";

const ExperienceRow = ({ experience, index, open, onToggle }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.08 }}
            viewport={{ once: true, margin: "-60px" }}
            className="border-b border-line"
        >
            <button
                onClick={onToggle}
                className="group flex w-full items-baseline justify-between gap-4 py-7 text-left"
                aria-expanded={open}
            >
                <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1">
                    <span className="font-mono text-xs text-faint">
                        0{index + 1}
                    </span>
                    <h3
                        className={`text-xl font-medium transition-colors sm:text-2xl ${
                            open ? "text-accent" : "text-fg group-hover:text-accent"
                        }`}
                    >
                        {experience.title}
                    </h3>
                    <span className="font-mono text-sm text-muted">
                        @ {experience.company}
                    </span>
                    {experience.tag && (
                        <span className="rounded-full bg-accent px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-base dark:text-[#0a0a0b]">
                            {experience.tag}
                        </span>
                    )}
                </div>
                <div className="flex shrink-0 items-baseline gap-5">
                    <span className="hidden font-mono text-xs text-muted sm:inline">
                        {experience.date}
                    </span>
                    <span
                        className={`font-mono text-lg text-accent transition-transform duration-300 ${
                            open ? "rotate-45" : ""
                        }`}
                    >
                        +
                    </span>
                </div>
            </button>

            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                    >
                        <div className="grid gap-8 pb-9 md:grid-cols-[2fr_1fr]">
                            <ul className="space-y-3">
                                {experience.points.map((point, i) => (
                                    <li
                                        key={i}
                                        className="flex gap-3 text-sm leading-relaxed text-muted"
                                    >
                                        <span className="mt-0.5 font-mono text-accent">
                                            →
                                        </span>
                                        {point}
                                    </li>
                                ))}
                            </ul>
                            <div>
                                <p className="mb-3 font-mono text-[11px] uppercase tracking-widest text-faint">
                                    Stack
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {experience.stack.map((tech) => (
                                        <span
                                            key={tech}
                                            className="rounded-full border border-line bg-elevated px-3 py-1 font-mono text-xs text-muted"
                                        >
                                            {tech}
                                        </span>
                                    ))}
                                </div>
                                <p className="mt-5 font-mono text-xs text-muted sm:hidden">
                                    {experience.date}
                                </p>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

const Experience = () => {
    const [openIndex, setOpenIndex] = useState(0);

    return (
        <section id="experience" className="py-28">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <SectionHeading
                    index="02"
                    label="Career Log"
                    title={
                        <>
                            Five years, one trajectory —{" "}
                            <span className="font-serif italic text-accent">
                                intern to senior
                            </span>
                            .
                        </>
                    }
                />

                <div className="border-t border-line">
                    {experiences.map((experience, index) => (
                        <ExperienceRow
                            key={index}
                            experience={experience}
                            index={index}
                            open={openIndex === index}
                            onToggle={() =>
                                setOpenIndex(openIndex === index ? -1 : index)
                            }
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Experience;